import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Brain, CheckCircle, XCircle, Warning, Question, PlusCircle, NotePencil, Trash, Sparkle, Microphone, SpeakerHigh } from "@phosphor-icons/react";
import { AdminHeader, Field, inputCls, PrimaryButton, GhostButton, LiveIndicator } from "@/components/admin/parts";
import { MarkdownLite } from "@/components/common/MarkdownLite";
import { MessageAttachments } from "@/components/common/MessageAttachments";
import { adminApi, getErrorMessage, API, ADMIN_TOKEN_KEY } from "@/lib/api";
import { ASSETS } from "@/lib/assets";
import { usePolling } from "@/hooks/usePolling";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { OutcomePicker } from "@/components/admin/OutcomePicker";

const RATINGS = [
  { key: "correct", label: "Correct", icon: CheckCircle, cls: "text-[#73bf44] border-[#73bf44]/40" },
  { key: "incorrect", label: "Incorrect", icon: XCircle, cls: "text-red-400 border-red-500/40" },
  { key: "partial", label: "Partially correct", icon: Warning, cls: "text-amber-400 border-amber-500/40" },
  { key: "needs_clarification", label: "Needs clarification", icon: Question, cls: "text-blue-400 border-blue-500/40" },
];

export default function ConversationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reviewMsg, setReviewMsg] = useState(null);

  const load = () => adminApi.get(`/admin/conversations/${id}`).then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // silently refresh so an open conversation updates live as new messages arrive
  usePolling(load, 15000);

  if (!data) return <div className="p-8 text-neutral-600">Loading...</div>;
  const { conversation: c, messages, feedback } = data;
  const notes = data.notes || [];

  return (
    <div>
      <AdminHeader
        title="Conversation Review"
        subtitle={`${c.chassis_number ? `${c.model || ""} · ${c.chassis_number}` : "No caravan linked"}${c.has_voice ? " · Includes voice chat" : ""}`}
        actions={
          <>
            <LiveIndicator />
            <GhostButton onClick={() => navigate("/admin/conversations")} data-testid="conversation-back"><ArrowLeft size={16} /> Back</GhostButton>
          </>
        }
      />
      <div className="p-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {messages.map((m) => (
            <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "assistant" && (
                <img src={ASSETS.nick} alt="Nick" className="w-8 h-8 rounded-full border border-neutral-700 shrink-0" />
              )}
              <div className={`max-w-[85%] ${m.role === "user" ? "" : ""}`}>
                <div
                  className={`rounded-2xl px-4 py-3 ${
                    m.role === "user"
                      ? "bg-[#73bf44] text-[#050505] rounded-tr-sm"
                      : "bg-[#141414] border border-neutral-800 text-neutral-200 rounded-tl-sm"
                  }`}
                >
                  {m.has_image && (
                    m.image_path ? (
                      <a
                        href={`${API}/messages/${m.id}/image?auth=${localStorage.getItem(ADMIN_TOKEN_KEY)}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="conversation-photo"
                      >
                        <img
                          src={`${API}/messages/${m.id}/image?auth=${localStorage.getItem(ADMIN_TOKEN_KEY)}`}
                          alt="Customer upload"
                          className="rounded-lg max-h-72 w-auto object-contain mb-2 border border-black/10 cursor-zoom-in"
                        />
                      </a>
                    ) : (
                      <div className="text-xs italic opacity-70 mb-1">Photo attached (not stored)</div>
                    )
                  )}
                  {m.role === "user" ? <div className="whitespace-pre-wrap">{m.content}</div> : <MarkdownLite text={m.content} />}
                </div>
                {m.role === "assistant" && (m.sources || []).length > 0 && (
                  <div data-testid="conversation-ai-attachments">
                    <MessageAttachments sources={m.sources} token={localStorage.getItem(ADMIN_TOKEN_KEY)} />
                  </div>
                )}
                <div className={`flex items-center gap-2 mt-1.5 flex-wrap ${m.role === "user" ? "justify-end" : ""}`}>
                  {m.via === "voice" && <VoiceTag role={m.role} />}
                  {m.role === "assistant" && m.confidence && (
                    <span className="text-[0.6rem] text-neutral-500 uppercase tracking-wide">confidence: {m.confidence}</span>
                  )}
                  {m.role === "assistant" && (
                    <button
                      onClick={() => setReviewMsg(m)}
                      data-testid="review-answer-button"
                      className="text-[0.65rem] text-[#73bf44] hover:underline flex items-center gap-1"
                    >
                      <Brain size={12} /> Review answer
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-6">
          <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl p-5">
            <h3 className="font-heading font-semibold text-white mb-3">Details</h3>
            <dl className="text-sm space-y-2">
              <Meta label="Conversation ID" value={c.id.slice(0, 8)} mono />
              <Meta label="Chassis" value={c.chassis_number || "—"} mono />
              <Meta label="Model" value={c.model || "—"} />
              <Meta label="Customer" value={c.customer_name || "—"} />
              <Meta label="Messages" value={c.message_count} />
              <Meta label="Channel" value={c.has_voice ? "Voice + text" : "Text"} />
            </dl>
            <div className="mt-4 pt-4 border-t border-neutral-800 space-y-2" data-testid="conversation-outcome">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-neutral-500">Outcome</span>
                <OutcomePicker conv={c} onChange={load} align="end" />
              </div>
              {c.outcome_reason && <p className="text-xs text-neutral-400 leading-relaxed" data-testid="conversation-outcome-reason">{c.outcome_reason}</p>}
              {c.customer_verdict && (
                <p className="text-xs text-neutral-500" data-testid="conversation-customer-verdict">
                  Customer said: <span className={c.customer_verdict === "solved" ? "text-[#73bf44]" : "text-amber-400"}>{c.customer_verdict === "solved" ? "it solved their issue" : "it didn't solve it"}</span>
                </p>
              )}
              {c.ticket_ref && (
                <button onClick={() => navigate(`/admin/support-tickets?q=${c.ticket_ref}`)} data-testid="conversation-ticket-link" className="text-xs font-mono text-sky-300 hover:underline">
                  Support ticket {c.ticket_ref} →
                </button>
              )}
            </div>
          </div>

          <NotesPanel conversationId={id} notes={notes} onChanged={load} />

          <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl p-5">
            <h3 className="font-heading font-semibold text-white mb-3">Feedback History</h3>
            {feedback.length === 0 ? (
              <p className="text-sm text-neutral-600">No feedback recorded yet.</p>
            ) : (
              <div className="space-y-3">
                {feedback.map((f) => (
                  <div key={f.id} className="border border-neutral-800 rounded-lg p-3">
                    <div className="text-xs font-semibold capitalize text-neutral-300">{f.rating.replace("_", " ")}</div>
                    {f.correct_answer && <div className="text-xs text-neutral-500 mt-1 line-clamp-3">{f.correct_answer}</div>}
                    {f.kb_entry_id && <div className="text-[0.6rem] text-[#73bf44] mt-1">✓ Added to knowledge base</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <ReviewDialog
        msg={reviewMsg}
        conversationId={id}
        onClose={() => setReviewMsg(null)}
        onSaved={() => { setReviewMsg(null); load(); }}
      />
    </div>
  );
}

function VoiceTag({ role }) {
  const Icon = role === "user" ? Microphone : SpeakerHigh;
  return (
    <span className="inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-wide text-[#73bf44]" data-testid="message-voice-tag">
      <Icon size={11} weight="fill" /> {role === "user" ? "Spoken · transcribed" : "Read aloud"}
    </span>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-neutral-500">{label}</dt>
      <dd className={`text-white text-right ${mono ? "font-mono text-xs" : ""}`}>{value}</dd>
    </div>
  );
}

function NotesPanel({ conversationId, notes, onChanged }) {
  const [text, setText] = useState("");
  const [teachAi, setTeachAi] = useState(true);
  const [title, setTitle] = useState("");
  const [applies, setApplies] = useState("");
  const [customerVisible, setCustomerVisible] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!text.trim()) { toast.error("Write a note first"); return; }
    setSaving(true);
    try {
      await adminApi.post(`/admin/conversations/${conversationId}/notes`, {
        note: text.trim(),
        teach_ai: teachAi,
        title: title.trim(),
        applies_to: applies.split(",").map((s) => s.trim()).filter(Boolean),
        customer_visible: customerVisible,
      });
      toast.success(teachAi ? "Note saved — Nick will learn from it" : "Note saved");
      setText(""); setTitle(""); setApplies(""); setCustomerVisible(false); setTeachAi(true);
      onChanged();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const del = async (n) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await adminApi.delete(`/admin/conversations/${conversationId}/notes/${n.id}`);
      toast.success("Note deleted");
      onChanged();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl p-5" data-testid="notes-panel">
      <div className="flex items-center gap-2 mb-3">
        <NotePencil size={18} className="text-[#73bf44]" weight="duotone" />
        <h3 className="font-heading font-semibold text-white">Notes</h3>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        data-testid="note-input"
        className={inputCls}
        placeholder="Add a comment or adjustment about this conversation…"
      />
      <label className="flex items-center gap-2 mt-3 cursor-pointer">
        <input type="checkbox" checked={teachAi} onChange={(e) => setTeachAi(e.target.checked)} className="accent-[#73bf44] w-4 h-4" data-testid="note-teach-ai" />
        <span className="text-sm text-neutral-300 flex items-center gap-1.5">
          <Sparkle size={14} className="text-[#73bf44]" /> Let Nick learn from this note
        </span>
      </label>

      {teachAi && (
        <div className="space-y-3 border-l-2 border-[#73bf44]/40 pl-4 mt-3">
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder="Knowledge title (optional)" data-testid="note-title" />
          <input value={applies} onChange={(e) => setApplies(e.target.value)} className={inputCls} placeholder="Applies to models (optional, comma separated)" />
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={customerVisible} onChange={(e) => setCustomerVisible(e.target.checked)} className="accent-[#73bf44] w-4 h-4" />
            <span className="text-sm text-neutral-300">Also visible to customers</span>
          </label>
        </div>
      )}

      <PrimaryButton onClick={save} disabled={saving} data-testid="note-save" className="w-full justify-center mt-4">
        <PlusCircle size={16} /> {saving ? "Saving…" : "Add Note"}
      </PrimaryButton>

      <div className="mt-4 space-y-2">
        {notes.length === 0 ? (
          <p className="text-sm text-neutral-600">No notes yet.</p>
        ) : (
          notes.map((n) => (
            <div key={n.id} className="border border-neutral-800 rounded-lg p-3" data-testid="note-item">
              {n.title && <div className="text-xs font-semibold text-white mb-1">{n.title}</div>}
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-neutral-200 whitespace-pre-wrap flex-1">{n.note}</p>
                <button onClick={() => del(n)} data-testid="note-delete" className="p-1 rounded text-neutral-500 hover:text-red-400 shrink-0"><Trash size={14} /></button>
              </div>
              {n.teach_ai && (
                <div className="text-[0.6rem] text-[#73bf44] mt-1.5 flex items-center gap-1">
                  <Sparkle size={11} weight="fill" /> Nick is learning from this note
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}


function ReviewDialog({ msg, conversationId, onClose, onSaved }) {
  const [rating, setRating] = useState("correct");
  const [note, setNote] = useState("");
  const [correctAnswer, setCorrectAnswer] = useState("");
  const [addKb, setAddKb] = useState(false);
  const [kbTitle, setKbTitle] = useState("");
  const [kbCategory, setKbCategory] = useState("");
  const [kbApplies, setKbApplies] = useState("");
  const [kbCustomer, setKbCustomer] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (msg) {
      setRating("correct"); setNote(""); setCorrectAnswer(""); setAddKb(false);
      setKbTitle(""); setKbCategory(""); setKbApplies(""); setKbCustomer(false);
    }
  }, [msg]);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.post("/admin/feedback", {
        conversation_id: conversationId,
        message_id: msg.id,
        rating,
        note,
        correct_answer: correctAnswer,
        add_to_kb: addKb,
        kb_title: kbTitle,
        kb_category: kbCategory,
        kb_applies_to: kbApplies.split(",").map((s) => s.trim()).filter(Boolean),
        kb_customer_visible: kbCustomer,
      });
      toast.success(addKb ? "Feedback saved & added to knowledge base" : "Feedback saved");
      onSaved();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!msg} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-white">
            <Brain size={20} className="text-[#73bf44]" /> Review Nick's Answer
          </DialogTitle>
        </DialogHeader>
        {msg && (
          <div className="space-y-4">
            <div className="bg-black/40 border border-neutral-800 rounded-lg p-3 text-xs text-neutral-400 max-h-28 overflow-y-auto">
              {msg.content}
            </div>

            <div className="grid grid-cols-2 gap-2">
              {RATINGS.map((r) => {
                const Icon = r.icon;
                const active = rating === r.key;
                return (
                  <button
                    key={r.key}
                    onClick={() => setRating(r.key)}
                    data-testid={`rating-${r.key}`}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                      active ? r.cls + " bg-white/[0.04]" : "border-neutral-800 text-neutral-400 hover:border-neutral-600"
                    }`}
                  >
                    <Icon size={16} /> {r.label}
                  </button>
                );
              })}
            </div>

            <Field label="Notes (optional)">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className={inputCls} placeholder="Internal notes about this answer..." data-testid="feedback-note" />
            </Field>

            <Field label="Correct / improved technical answer" hint="Provide the accurate answer. This can be saved as approved OTM knowledge.">
              <textarea value={correctAnswer} onChange={(e) => setCorrectAnswer(e.target.value)} rows={4} className={inputCls} placeholder="e.g. The PM335 maximum solar input is 30A..." data-testid="feedback-correct-answer" />
            </Field>

            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={addKb} onChange={(e) => setAddKb(e.target.checked)} className="accent-[#73bf44] w-4 h-4" data-testid="feedback-add-kb" />
              <span className="text-sm text-neutral-300 flex items-center gap-1.5"><PlusCircle size={16} className="text-[#73bf44]" /> Add this as an approved knowledge-base entry</span>
            </label>

            {addKb && (
              <div className="space-y-3 border-l-2 border-[#73bf44]/40 pl-4">
                <Field label="Entry title"><input value={kbTitle} onChange={(e) => setKbTitle(e.target.value)} className={inputCls} placeholder="e.g. PM335 Solar Input Configuration" data-testid="feedback-kb-title" /></Field>
                <Field label="Category"><input value={kbCategory} onChange={(e) => setKbCategory(e.target.value)} className={inputCls} placeholder="Electrical > Solar > PM335" /></Field>
                <Field label="Applies to (comma separated)"><input value={kbApplies} onChange={(e) => setKbApplies(e.target.value)} className={inputCls} placeholder="Adventure, Grenade, Storm" /></Field>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={kbCustomer} onChange={(e) => setKbCustomer(e.target.checked)} className="accent-[#73bf44] w-4 h-4" />
                  <span className="text-sm text-neutral-300">Also visible to customers</span>
                </label>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <GhostButton onClick={onClose}>Cancel</GhostButton>
              <PrimaryButton onClick={save} disabled={saving} data-testid="feedback-save">{saving ? "Saving..." : "Save Feedback"}</PrimaryButton>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
